import React, {memo, useEffect, useState} from 'react'
import cx from 'classnames'

/* Config */
import appConfig from '../../../../appConfig'

/* Components */
import VoltranFragment from '../../../../components/VoltranFragment/VoltranFragment'
import ReturnPolicy from './returnPolicy/ReturnPolicy'

/* Utils */
import DataSet from '../../../../utils/DataSet'
import {resolveWithSettleContext} from '../../../../common/async/promiseUtils'
import {yieldToMain} from '../../../../utils/performance'

/* Actions */
import {actionCreators as productActionsCreators} from '../../../../redux/actions/productActions'
import {actionCreators as voltranActionCreators} from '../../../../redux/actions/voltranActions'
import {actionCreators as modalActionCreators} from '../../../../redux/actions/modalActions';

/* Constants */
import {DESKTOP_COMPONENTS, DESKTOP_COMPONENTS_OPTIONS} from '../../../../modules/voltran/voltranConstants'
import {TAB_CLICK_TYPES, TAB_EVENT_LISTENER, TAB_IDS} from '../../../../modules/product/productDesktopConstants'
import { MODAL_IDS } from '../../../../modules/modal/modalConstants'

/* Styles */
import s from './tab.scss'

const Tab = memo(
  ({
    className,
    dispatch,
    tabList,
    product,
    sku,
    categories,
    price,
    merchantId,
    merchantName,
    listingId,
    productId,
    categoryId,
    categoryName,
    categoryUrlKeyword,
    pageTypeForDavinci,
    currentPriceBeforePoint,
    currentPriceAfterPoint,
    tabActiveIndex,
    enableErrorFeedbackButton,
    campaignEvaluateResult,
    campaignEvaluateFinalPrice
  }) => {
    const [clientSideComponents, setClientSideComponents] = useState(tabList?.filter?.(item => !item.component))

    useEffect(() => {
      window.tabListNames = tabList?.map?.((item) => {
        return {
          id: item.id,
          load: Boolean(item?.component),
        }
      })

      window?.HbEventBus?.on(TAB_EVENT_LISTENER, (data) => {
        tabClickHandler(data?.index, data?.id).then(() => {
          requestAnimationFrame(() => {
            document.getElementById('tab')?.scrollIntoView({behavior: 'smooth'})
          })
        })
      })
    }, [])

    const tabClickHandler = async(index, id, item) => {
      if (tabActiveIndex === index) {
        return
      }
      window.tabListNames[index].load = true

      let findIndex = clientSideComponents?.findIndex(clientSideComponentsItem => clientSideComponentsItem?.id === id)

      if (item?.component || clientSideComponents?.[findIndex]?.component) {
        dispatch(
          productActionsCreators.setDesktopTabs.trigger(index)
        )
      } else {
        fetchClientSideComponent(id, index)
      }
      await yieldToMain()
      tabClickTrackEvent(id)
    }

    const tabClickTrackEvent = (id) => {
      let data = {
        page_type: pageTypeForDavinci,
        page_value: sku,
        category_hierarchy:
          categories?.map?.((category) => ({
            id: category?.categoryId,
            name: category?.categoryName,
          })) || [],
        merchant: {
          id: merchantId,
          name: merchantName,
        },
        category: {
          id: categoryId,
          name: categoryUrlKeyword,
        },
        products: [
          {
            listing_id: listingId,
            position: 1,
            price,
            product_id: productId,
            product_status: '',
            shipping_type: '',
            sku,
          },
        ],
      }

      window?.hepsiBus?.track(TAB_CLICK_TYPES[id], data)
    }

    const errorReportingClickTrackEvent = (id) => {
      let data = {
        page_type: pageTypeForDavinci,
        page_value: sku,
        category_hierarchy:
          categories?.map?.((category) => ({
            id: category?.categoryId,
            name: category?.categoryName,
          })) || [],
        merchant: {
          id: merchantId,
          name: merchantName,
        },
        category: {
          id: categoryId,
          name: categoryUrlKeyword,
        },
        products: [
          {
            listing_id: listingId,
            position: 1,
            price,
            product_id: productId,
            product_status: '',
            shipping_type: '',
            sku,
          },
        ],
      }

      window?.hepsiBus?.track('ErrorReportingClick', data)
    }

    const getReturnPolicyInformation = async() => {
      await yieldToMain()
      let returnPolicy = await resolveWithSettleContext(
        dispatch(
          productActionsCreators.getProductReturnPolicy.trigger(
            sku,
          )
        )
      )
      return returnPolicy
    }

    const loadComponent = (index, component) => {
      const loadedComponents = [...clientSideComponents]
      loadedComponents[index] = {
        ...clientSideComponents[index],
        component
      }
      setClientSideComponents(loadedComponents)
    }

    const fetchClientSideComponent = async(id, index) => {
      let findIndex = clientSideComponents?.findIndex(clientSideComponentsItem => clientSideComponentsItem?.id === id)
      let component
      switch (id) {
        case TAB_IDS.REVIEWS:
          component = await fetchComponentHelper({
            options: DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.USER_CONTENT],
            paramater: {
              baseUrl: appConfig.baseUrl + encodeURI(window?.location.pathname + window?.location.search),
              sku: product?.sku,
              platform: 'desktop',
              hasOneVariant: product?.hasVariants ? undefined : true
            }
          })
          break
        case TAB_IDS.QUESTION_ANSWER:
          component = await fetchComponentHelper({
            options: DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.QUESTION_AND_ANSWER],
            paramater: {
              sku: sku,
              merchantId: merchantId,
              productId: productId,
            }
          })
          break
        case TAB_IDS.BNPL:
          component = <div>BNPL</div>
          break
        case TAB_IDS.INSTALLMENTS:
          let availableFinalPrice = campaignEvaluateResult?.evaluateResult || campaignEvaluateResult?.evaluateResult || window?.campaignFinalPrice || null;
          let finalPrice = Number(campaignEvaluateResult?.evaluateAsPremiumResult?.discountedAmount) || Number(campaignEvaluateResult?.evaluateResult?.discountedAmount) || Number(window?.campaignFinalPrice);
          let finalPriceFormatted = Math.round(finalPrice * 100).toString();

          component = await fetchComponentHelper({
            options: DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.INSTALLMENT],
            paramater: {
              amount: availableFinalPrice ? finalPriceFormatted : (currentPriceBeforePoint + currentPriceAfterPoint).toString().replace('.', '').replace(',', ''),
              maxInstallment: product?.numberOfInstallments,
              paymentTag: product?.paymentTag,
              taxRatio: product?.taxVatRate,
              definitionId: product?.definitionId,
              merchantId: merchantId
            }
          })
          break
        case TAB_IDS.LOAN_TAB:
          component = await fetchComponentHelper({
            options: DESKTOP_COMPONENTS_OPTIONS[DESKTOP_COMPONENTS.LOAN],
            paramater: {
              amount: (currentPriceBeforePoint + currentPriceAfterPoint).toString().replace('.', '').replace(',', ''),
              maxInstallment: product?.numberOfInstallments,
              paymentTag: product?.paymentTag,
              taxRatio: product?.taxVatRate,
              definitionId: product?.definitionId,
              merchantId: merchantId,
              consumerFinanceTagList: product?.consumerFinanceTagList
            },
          })
          break
        case TAB_IDS.RETURN_POLICY:
          try {
            const data = await getReturnPolicyInformation(id)
            component = <ReturnPolicy returnPolicy={data}/>
          } catch (error) {
            console.error('Error', error)
          }
          break
        default:
          break
      }

      if (component) {
        loadComponent(findIndex, component)
        dispatch(
          productActionsCreators.setDesktopTabs.trigger(index)
        )
      }
    }

    const fetchComponentHelper = async({options, paramater}) => {
      const component = await resolveWithSettleContext(
        dispatch(voltranActionCreators.getFragments.trigger([{
          ...options,
          paramater: paramater,
        }]))
      )
      return (
        <VoltranFragment
          fragmentInfo={component?.result?.[0]?.result?.data}
          options={options}
          renderMethodClientSide={process.env.BROWSER}
          removeClientStyles/>
      )
    }

    return (
      <section className={cx(s.root, className)} data-test-id={'tab'} id={'tab'} aria-label='Ürün Detayları'>
        <div className={s.tabHeads} role='tablist' aria-label='Ürün Detay Sekmeleri'>
          {
            tabList?.map?.((item, index) => {
              return (
                <button
                  className={cx(s.headTitleContainer,s.headTitle,
                    tabActiveIndex === index ? s.active : null
                  )}
                  role='tab'
                  key={item?.id}
                  aria-selected={tabActiveIndex === index}
                  aria-controls={item?.id}
                  aria-label={`${tabActiveIndex === index ? 'Seçili':''} ${tabList?.[index]?.title} ${item?.notification ? item?.notification : ''}`}
                  data-hbus={DataSet.createComponentViewDataForDavinci({
                    pageType: pageTypeForDavinci,
                    pageValue: sku,
                    placement: 'Tab',
                    component: item.id,
                    componentDetail: item.title,
                    categoryHierarchy: `${categoryId} - ${categoryName}`,
                    position: index + 1,
                    productId,
                    merchantName,
                    merchantId,
                    listingId,
                    price: `${price}`,
                  })}
                  onClick={() => tabClickHandler(index, item?.id, item)}>
                  {item?.title}
                  {
                    item?.notification &&
                    <div className={s.headBadge}>{item?.notification}</div>
                  }
                </button>
              )
            })
          }
        </div>
        <div className={s.tabContentContainer}>
          {
            tabList?.map?.((item, index) => {
              let findIndex = clientSideComponents?.findIndex(clientSideComponentsItem => clientSideComponentsItem?.id === item?.id)
              let component = item?.component || clientSideComponents[findIndex].component
              return (
                <div
                  key={item?.id}
                  className={cx(
                    s.tabContent,
                    tabActiveIndex === index ? s.active : null
                  )}
                  role='tabpanel'
                  id={item?.id}
                  style={{display: tabActiveIndex === index ? 'block' : 'none'}}>
                  <div>
                    {component || null}
                  </div>
                  {
                    (item.id === TAB_IDS.DESCRIPTION && enableErrorFeedbackButton) ?
                    <div className={s.errorPopupButtonContainer}>
                      <div className={s.errorPopupButton}
                        data-hbus={
                          DataSet.createComponentViewDataForDavinci({
                            pageType: pageTypeForDavinci,
                            pageValue: sku,
                            placement: 'Error Reporting',
                            component: 'Error Reporting',
                            categoryHierarchy: `${categoryId} - ${categoryName}`,
                            productId: productId,
                            merchantName: merchantName,
                            merchantId: merchantId,
                            listingId: listingId,
                            price: `${price}`,
                          })
                        }
                        onClick={async() => {
                          await yieldToMain()
                          dispatch(modalActionCreators.openModal(MODAL_IDS.ERROR_POPUP))
                          errorReportingClickTrackEvent()
                        }}>
                          <div className={s.errorIcon} />
                          Hatalı içerik bildir
                      </div>
                    </div>
                    : null
                  }
                </div>
              )
            })
          }
        </div>
      </section>
    )
  }
)

export default Tab
